/*
 * Die Rasterflaeche des HMI-Editors, Maschinenseite: Uebersetzung zwischen
 * dem portablen Schema und Gridstack, dazu Mounten und Abraeumen einer
 * Instanz.
 *
 * GRIDSTACK TREIBT NUR DIE INTERAKTION. Gespeichert wird ausschliesslich
 * `ScreenLayout.placements` mit col/row/colSpan/rowSpan je LayoutTarget.
 * Gridstack-eigener Zustand wird NIE persistiert, nicht einmal nebenbei.
 * Das ist bewusst anders als beim Dashboard (`layouts/gridLayoutHost.ts`),
 * das `engineState` ablegt und damit gut faehrt, weil ein Dashboard nur im
 * Browser existiert. Ein HmiProject existiert nicht nur im Browser: dasselbe
 * Projekt rendert die Kotlin-Panel-App, und die kennt kein Gridstack. Eine
 * Anordnung, die nur im Engine-Format vorliegt, waere auf dem Panel keine
 * Anordnung, sondern ein leeres Bild.
 *
 * DARUM IST DIE UEBERSETZUNG DIE HEIKELSTE STELLE HIER. Ein Fehler darin
 * erzeugt keine Fehlermeldung, sondern verliert Arbeit: der Autor schiebt,
 * sieht das Ergebnis, speichert, und beim naechsten Oeffnen steht es
 * woanders. Deshalb sind {@link toGridItems} und {@link toPlacements} reine
 * Funktionen, und der Rundlauf ist in `tests/hmiGridHost.test.ts`
 * festgenagelt.
 *
 * KEINE LAUFZEIT-IMPORTE IN DIESER DATEI, und Gridstack wird erst beim
 * Mounten geladen. Der Grund ist der Test: `npm test` faehrt die Dateien
 * direkt mit Node (`node --experimental-strip-types`), ohne Bundler. Node
 * loest weder `gridstack` auf (die Bibliothek importiert intern ohne
 * Dateiendung) noch einen endungslosen Pfad auf eine `.ts`-Datei. Ein
 * gewoehnlicher Import hier haette die reinen Funktionen fuer den Test
 * unerreichbar gemacht, und ein Test, der nicht laeuft, schuetzt nichts.
 * Praktisch heisst das: `GridSpec` und `WidgetPlacement` kommen als
 * Typ-Import (den Node wegstreicht), das Rastermass reicht der Aufrufer als
 * Wert herein (`GRID_SPECS[target]`), und Gridstack holt sich
 * {@link mountHmiGrid} per dynamischem Import, der nur im Browser laeuft.
 *
 * WER DIE GRENZEN DURCHSETZT: `clampPlacement` in `editorModel.ts`, und nur
 * das. Die Funktionen hier bauen nichts, was offensichtlich daneben liegt,
 * aber sie sind nicht die zweite Pruefstelle. Alles, was diese Datei
 * meldet, geht ueber `setPlacements` ins Projekt, und das klemmt jede
 * Platzierung ins Raster des Ziels.
 */

import type { GridSpec } from './editorModel';
import type { WidgetPlacement } from '../widgets';

// ------------------------------------------------------------ Datenformen

/**
 * Ein Gridstack-Item, wie wir es hineingeben: vollstaendig.
 *
 * Absichtlich eine eigene Form und nicht `GridStackWidget`. Der Typ von
 * Gridstack hat drei Dutzend optionale Felder, und die Uebersetzung soll
 * genau die fuenf betreffen, die es ins Schema schaffen. Was hier nicht
 * steht, kann auch nicht versehentlich persistiert werden.
 */
export interface GridItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Ein Gridstack-Item, wie es zurueckkommt: alles optional.
 *
 * `grid.save()` laesst Felder weg, die dem Standardwert entsprechen, und
 * eine fehlende `x`-Angabe heisst dort Spalte 0 und nicht "unbekannt". Wer
 * das nicht auffuellt, schreibt `undefined` ins Projekt und wundert sich
 * spaeter ueber ein Widget in der linken oberen Ecke.
 */
export interface RawGridItem {
  id?: string | null;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

// ---------------------------------------------------------- Uebersetzung

/**
 * Schema nach Gridstack.
 *
 * Die Sortierung ist kein Schoenheitsdienst. Gridstack legt beim Laden in
 * der Reihenfolge der Liste ab, und bei `float: false` haengt das Ergebnis
 * davon ab; in Lesereihenfolge geladen kommt heraus, was der Autor sieht.
 * Die Id als letztes Kriterium macht die Ausgabe auch bei deckungsgleichen
 * Platzierungen reproduzierbar, sonst haette derselbe Stand je nach
 * Schluesselreihenfolge des Objekts zwei Darstellungen.
 */
export function toGridItems(placements: Readonly<Record<string, WidgetPlacement>>): GridItem[] {
  return Object.entries(placements)
    .map(([id, p]) => ({ id, x: p.col, y: p.row, w: p.colSpan, h: p.rowSpan }))
    .sort((a, b) => a.y - b.y || a.x - b.x || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * Gridstack nach Schema.
 *
 * Items ohne Id werden verworfen. Eine Platzierung ohne Widget-Id waere ein
 * Eintrag, den niemand mehr zuordnen kann, und `setPlacements` wuerde ihn
 * ohnehin wegwerfen: besser hier sichtbar an einer Stelle als dort still.
 * Breite und Hoehe bekommen mindestens 1, weil ein Widget mit Ausdehnung 0
 * auf dem Panel nicht klein waere, sondern weg.
 */
export function toPlacements(items: readonly RawGridItem[]): Record<string, WidgetPlacement> {
  const out: Record<string, WidgetPlacement> = {};
  for (const item of items) {
    if (!item.id) continue;
    out[item.id] = {
      col: Math.max(0, Math.round(item.x ?? 0)),
      row: Math.max(0, Math.round(item.y ?? 0)),
      colSpan: Math.max(1, Math.round(item.w ?? 1)),
      rowSpan: Math.max(1, Math.round(item.h ?? 1)),
    };
  }
  return out;
}

/**
 * Vergleichsschluessel einer Anordnung.
 *
 * Damit wird das Echo erkannt: nach einem Zug meldet das Raster den neuen
 * Stand, der Editor schreibt ihn ins Projekt, und das Projekt schiebt ihn
 * postwendend wieder ins Raster. Ohne diesen Vergleich laedt sich Gridstack
 * seinen eigenen Stand neu, moeglicherweise mitten in der naechsten Geste,
 * und das sieht man als Ruckeln.
 *
 * Verlaesst sich darauf, dass {@link toGridItems} sortiert liefert; zwei
 * gleiche Anordnungen ergeben denselben String.
 */
export function signatureOf(items: readonly GridItem[]): string {
  return items.map((i) => `${i.id}:${i.x},${i.y},${i.w},${i.h}`).join('|');
}

// ------------------------------------------------------------ Anfangslage

/**
 * Nachvollziehbare Anfangsanordnung fuer Widgets ohne Platzierung.
 *
 * Der Fall ist der Normalfall und kein Fehler: ein Screen, dessen Autor
 * dieses Ziel noch nicht bearbeitet hat, hat fuer dieses Ziel keinen
 * Layout-Eintrag. Die Flaeche muss trotzdem etwas zeigen, sonst sieht der
 * Autor ein leeres Bild und haelt seine Widgets fuer verloren.
 *
 * Bewusst stur von links nach rechts in der Reihenfolge der Widget-Ids des
 * Screens, feste Groesse, Umbruch am Spaltenende. Kein Packen, kein Raten
 * nach Widget-Typ. Eine geratene Anordnung sieht aus wie eine gemachte, und
 * der Autor wuerde sie nicht mehr pruefen. Das Gegenmittel gegen diese
 * Verwechslung ist nicht der Algorithmus, sondern die Kennzeichnung in der
 * Oberflaeche, plus die Regel, dass diese Anordnung erst dann ins Projekt
 * kommt, wenn der Autor etwas anfasst.
 *
 * `startRow` setzt den Block unter bereits platzierte Nachbarn, damit ein
 * neu hinzugekommenes Widget nicht unter einem vorhandenen verschwindet.
 */
export function autoFlowPlacements(
  widgetIds: readonly string[],
  spec: GridSpec,
  startRow = 0,
): Record<string, WidgetPlacement> {
  // Ein Viertel der Breite, aber nie schmaler als zwei Spalten: auf dem
  // Handy mit vier Spalten waere ein Viertel eine Saeule, in der kein
  // Messwert lesbar steht.
  const colSpan = Math.max(2, Math.min(spec.columns, Math.round(spec.columns / 4)));
  const rowSpan = 2;
  const perRow = Math.max(1, Math.floor(spec.columns / colSpan));

  const out: Record<string, WidgetPlacement> = {};
  widgetIds.forEach((id, index) => {
    const col = (index % perRow) * colSpan;
    let row = Math.max(0, startRow) + Math.floor(index / perRow) * rowSpan;
    if (spec.rows !== null) {
      // Auf einem festen Panel ist unterhalb der letzten Zeile nichts. Die
      // ueberzaehligen Widgets stapeln sich sichtbar in der letzten Zeile,
      // statt aus dem Bild zu fallen: eng ist diagnostizierbar, weg nicht.
      row = Math.min(row, Math.max(0, spec.rows - rowSpan));
    }
    out[id] = { col: Math.min(col, Math.max(0, spec.columns - colSpan)), row, colSpan, rowSpan };
  });
  return out;
}

// ------------------------------------------------------------- Mausposition

/**
 * Zelle unter einem Punkt, gemessen relativ zur linken oberen Ecke der
 * Rasterflaeche.
 *
 * Fuer das Ablegen aus der Palette. Die Rechnung ignoriert die Raender
 * zwischen den Zellen, das Ergebnis ist also auf eine halbe Randbreite genau
 * und nicht exakt. Genauer waere es nur mit Gridstack-Interna, und der Autor
 * zieht das abgelegte Widget ohnehin auf seinen Platz: eine exakte Rechnung
 * mit einer Abhaengigkeit auf fremde Interna kostet mehr, als sie hier
 * bringt.
 */
export function cellAtPoint(
  offset: { x: number; y: number },
  area: { width: number; rowHeightPx: number },
  spec: GridSpec,
): { col: number; row: number } {
  const cellWidth = area.width > 0 ? area.width / spec.columns : 0;
  const col = cellWidth > 0 ? Math.floor(offset.x / cellWidth) : 0;
  const row = area.rowHeightPx > 0 ? Math.floor(offset.y / area.rowHeightPx) : 0;

  const lastRow = spec.rows !== null ? spec.rows - 1 : Number.MAX_SAFE_INTEGER;
  return {
    col: Math.max(0, Math.min(col, spec.columns - 1)),
    row: Math.max(0, Math.min(row, lastRow)),
  };
}

// ------------------------------------------------------------- Rasteroptionen

/** Die Gridstack-Optionen, die wir setzen. Eigener Typ statt
 *  `GridStackOptions`, damit diese Datei ohne Laufzeit-Import auskommt (siehe
 *  Kopf). Beim Mounten wird daraus das, was Gridstack erwartet. */
export interface HmiGridOptions {
  column: number;
  cellHeight: number;
  margin: number;
  float: boolean;
  animate: boolean;
  acceptWidgets: boolean;
  resizable: { handles: string };
  maxRow?: number;
}

/**
 * Rasteroptionen aus dem Rastermass eines Ziels.
 *
 * `maxRow` nur dort, wo `rows` eine Zahl ist. Ein Panel hat keinen
 * Scrollbalken; ein Widget unterhalb der letzten Zeile ist dort nicht schwer
 * zu finden, sondern nicht vorhanden. Gridstack laesst es dann gar nicht
 * erst dorthin, und der Autor merkt es beim Ziehen statt beim Kunden.
 *
 * `float: true` ist der Unterschied zum Dashboard, und er ist Absicht. Ein
 * Dashboard ist eine Liste von Kacheln, die nach oben rutschen duerfen. Ein
 * Anlagenbild ist ein Bild: wer den Ruecklauf unter den Vorlauf legt, meint
 * genau das, und ein Widget, das nach dem Loslassen nach oben springt, macht
 * aus der Zeichnung ein Verhandeln mit der Bibliothek.
 */
export function gridOptionsFor(spec: GridSpec): HmiGridOptions {
  const options: HmiGridOptions = {
    column: spec.columns,
    cellHeight: spec.cellHeightPx,
    // Gridstack rechnet in Pixeln und kann keine CSS-Variable lesen; 8 ist
    // der Zahlenwert von `--space-2` und damit derselbe Abstand, den das
    // Dashboard zeigt.
    margin: 8,
    float: true,
    animate: true,
    // Die Palette liefert per HTML5-Drag-and-Drop, nicht ueber Gridstacks
    // eigenes dragIn. Das Anlegen kennt die typspezifischen Pflichtfelder
    // und gehoert nicht hierher, also nimmt das Raster auch nichts an.
    acceptWidgets: false,
    // Nur die drei Griffe rechts und unten. Die oberen Kanten liegen auf der
    // Flaeche, an der der Autor das Widget packt, um es zu verschieben; beide
    // Gesten am selben Pixel heisst, dass eine davon danebengeht.
    resizable: { handles: 'e,s,se' },
  };
  if (spec.rows !== null) options.maxRow = spec.rows;
  return options;
}

// ------------------------------------------------------------------ Mounten

/** Rueckmeldungen der Rasterflaeche an den Editor. */
export interface HmiGridCallbacks {
  /**
   * Der KOMPLETTE Stand des Rasters nach einer Interaktion.
   *
   * Vollstaendig und nicht nur das bewegte Widget, weil ein Zug auch
   * Nachbarn verschiebt (die Begruendung steht ausfuehrlich in
   * `setPlacements`). `gestureKey` bleibt ueber einen Zug hinweg gleich und
   * gehoert in den `coalesceKey` von `apply`, sonst ist ein einziges Ziehen
   * dreissig Schritte in der Rueckgaengig-Kette.
   */
  onLayoutChange(placements: Record<string, WidgetPlacement>, gestureKey: string): void;
  /** Das Raster steht und die Item-Elemente existieren. Erst danach kann
   *  jemand Inhalte hineinrendern. */
  onReady?(): void;
  /**
   * Die Rasterbibliothek liess sich nicht laden.
   *
   * Muss der Aufrufer anzeigen. Eine Meldung nur auf der Konsole waere hier
   * dasselbe wie keine: die Flaeche bliebe leer, und eine leere Flaeche ist
   * von "dieses Bild hat noch keine Widgets" nicht zu unterscheiden. Der
   * Autor wuerde weiterarbeiten und sich wundern, warum nichts erscheint.
   */
  onError?(error: unknown): void;
}

export interface HmiGridHandle {
  /** Das `.grid-stack`-Element. Gehoert dem Host; der Aufrufer darf darin
   *  die Inhalte der `.grid-stack-item-content` rendern, sonst nichts. */
  readonly element: HTMLElement;
  /**
   * Fremden Stand ins Raster schieben.
   *
   * Fuer alles, was das Projekt aendert, ohne dass jemand gezogen hat:
   * Rueckgaengig, Tastatur, Loeschen, ein neues Widget aus der Palette.
   * Loest bewusst KEIN `onLayoutChange` aus, sonst schriebe das Raster den
   * Stand, den es gerade bekommen hat, als neue Aenderung zurueck und jeder
   * Schritt haette einen Zwilling in der Historie.
   */
  sync(items: readonly GridItem[]): void;
  destroy(): void;
}

type GridStackInstance = import('gridstack').GridStack;
type GridStackOptionsType = import('gridstack').GridStackOptions;
type GridStackWidgetType = import('gridstack').GridStackWidget;

/**
 * Gridstack-Instanz in `container` aufbauen.
 *
 * Synchron, obwohl Gridstack asynchron geladen wird: der Aufrufer bekommt
 * sofort ein Handle mit `destroy`, und ein Abraeumen vor dem Eintreffen der
 * Bibliothek ist damit ein normaler Fall und kein Wettlauf. Genau daran
 * haengt der Ziel- und Bildwechsel, und ein Leck dort zeigt sich als Widget,
 * das nach dem dritten Wechsel doppelt haengt.
 */
export function mountHmiGrid(
  container: HTMLElement,
  spec: GridSpec,
  items: readonly GridItem[],
  callbacks: HmiGridCallbacks,
): HmiGridHandle {
  // Gridstack braucht ein eigenes Element mit der Klasse `.grid-stack`. Wir
  // legen es an und raeumen es weg, damit der Container dem Aufrufer gehoert
  // und nach dem Abraeumen wieder so aussieht wie vorher.
  const gridEl = document.createElement('div');
  gridEl.className = 'grid-stack';
  container.appendChild(gridEl);

  let grid: GridStackInstance | null = null;
  let disposed = false;
  let gesture = 0;
  // Waehrend wir selbst laden, ist jede Meldung des Rasters ein Echo unserer
  // eigenen Aenderung und keine Eingabe des Autors.
  let syncing = false;
  let pending: readonly GridItem[] | null = null;
  // Der Stand, den das Raster zuletzt hatte oder gemeldet hat. Siehe
  // {@link signatureOf}: ohne diesen Vergleich laedt sich das Raster sein
  // eigenes Echo neu.
  let held = signatureOf(items);

  const readPlacements = (g: GridStackInstance): Record<string, WidgetPlacement> =>
    toPlacements((g.save(false, false) as GridStackWidgetType[]) as RawGridItem[]);

  const loadItems = (g: GridStackInstance, next: readonly GridItem[]): void => {
    syncing = true;
    try {
      g.load(next as GridStackWidgetType[], true);
      held = signatureOf(next);
    } finally {
      syncing = false;
    }
  };

  void import('gridstack')
    .then(({ GridStack }) => {
      if (disposed) return;

      const g = GridStack.init(gridOptionsFor(spec) as GridStackOptionsType, gridEl);
      grid = g;

      // Erst laden, dann lauschen. Gridstack meldet die eigenen Zugaenge
      // synchron waehrend `load`, und diese Meldung ist kein Zug des Autors:
      // wer hier zu frueh lauscht, schreibt beim blossen Oeffnen eines
      // Bildes ein Layout ins Projekt, das niemand angelegt hat.
      loadItems(g, pending ?? items);
      pending = null;

      // Nur am ANFANG einer Geste hochzaehlen, nicht am Ende. Gridstack
      // meldet `change` erst nach `dragstop`; wer dort ebenfalls hochzaehlt,
      // gibt ausgerechnet der letzten und wichtigsten Meldung eines Zuges
      // einen eigenen Schluessel und zerlegt den Zug damit in zwei Schritte.
      // Eine Meldung ohne vorangegangene Geste (Gridstack raeumt nach einem
      // maxRow-Stoss nach) faellt so mit dem Zug zusammen, der sie ausgeloest
      // hat, und das ist genau richtig.
      g.on('dragstart resizestart', () => {
        gesture += 1;
      });
      g.on('change added removed', () => {
        if (syncing || disposed) return;
        const placements = readPlacements(g);
        held = signatureOf(toGridItems(placements));
        callbacks.onLayoutChange(placements, `zug-${gesture}`);
      });

      callbacks.onReady?.();
    })
    .catch((err) => {
      // Sichtbar, nicht still. Ohne Gridstack bleibt die Flaeche leer, und
      // eine leere Flaeche ist von "dieses Bild hat keine Widgets" nicht zu
      // unterscheiden. Die Konsole allein waere deshalb keine Meldung,
      // sondern nur eine Spur fuer hinterher.
      console.error('[hmiGridHost] Gridstack konnte nicht geladen werden.', err);
      if (!disposed) callbacks.onError?.(err);
    });

  return {
    element: gridEl,
    sync(next: readonly GridItem[]): void {
      if (disposed) return;
      if (signatureOf(next) === held) return;
      if (!grid) {
        pending = next;
        return;
      }
      loadItems(grid, next);
    },
    destroy(): void {
      if (disposed) return;
      disposed = true;
      if (grid) {
        grid.off('change added removed');
        grid.off('dragstart resizestart');
        // `destroy(false)` laesst das Item-DOM stehen; das raeumt die Zeile
        // darunter mit dem Element weg, in dem es haengt.
        grid.destroy(false);
        grid = null;
      }
      gridEl.remove();
    },
  };
}
